
import React, { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Invoice, TaxPayableInfo } from "@/types/payments";
import { Badge } from "@/components/ui/badge";
import DocumentViewerPanel from "@/components/shared/DocumentViewerPanel";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { hasVendorAccount } from "@/utils/formatVendorAccount";
import SekEquivalent from "@/components/shared/SekEquivalent";
import { usePaymentVerificationDisplay } from "@/i18n/usePaymentVerificationDisplay";
import { useStatusBadge } from "@/i18n/useStatusBadge";
import { useFormatters } from "@financialharmony/ui";
import { useReceiptLabels } from "@/components/receipts/useReceiptLabels";
import { LaterMatchFlag } from "@/components/shared/PaymentVerificationBadge";
import { Link } from "react-router-dom";
import { useTaxPayableCaption } from "@/i18n/useTaxTransactionLabel";
import { useFormatOptionalDate } from "@/i18n/useFormatOptionalDate";

type PartyKind = "payable" | "receivable" | "receipt";
const PARTY_LABEL_KEY: Record<PartyKind, "vendor" | "customer" | "merchant"> = {
  payable: "vendor",
  receivable: "customer",
  receipt: "merchant",
};

function partyKindOf(documentType: string | undefined): PartyKind {
  return documentType === "receivable" || documentType === "receipt" ? documentType : "payable";
}

interface InvoiceDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  handlePayNow?: (invoiceId: string) => void;
  updatePaymentDate?: (invoiceId: string, date: Date) => void;
  formatCurrency: (amount: number, currency: string) => string;
}

/**
 * S3 (FIN-977): a tax payable opens like any payable, plus what it was made from. Its amount is
 * the announced item's; once signing has started the payable is kept even if the item changes,
 * and the note says what Skatteverket announces now.
 */
const TaxPayableDetails: React.FC<{
  invoice: Invoice;
  tax: TaxPayableInfo;
  formatCurrency: (amount: number, currency: string) => string;
}> = ({ invoice, tax, formatCurrency }) => {
  const t = useTranslations("consultantPayments.details.tax");
  const taxCaption = useTaxPayableCaption();
  // A payable with no amount has nothing to compare, so it never reads as changed (FIN-413).
  const changedSinceSigning = invoice.status !== "unpaid" && invoice.status !== "overdue" &&
    invoice.status !== "cancelled" && invoice.amount !== null && invoice.amount !== undefined &&
    Math.round(tax.itemAmount * 100) !== Math.round(invoice.amount * 100);
  return (
    <div className="space-y-3" data-testid="tax-payable-details">
      <h4 className="text-sm font-semibold">
        {taxCaption(tax.transactionType, tax.description)}
      </h4>
      <p className="text-sm text-muted-foreground">
        {t(invoice.documentType === "receivable" ? "receivableNote" : "payableNote")}
      </p>
      {tax.withdrawn && (
        <p className="text-sm text-amber-700">{t("withdrawn")}</p>
      )}
      {changedSinceSigning && (
        <p className="text-sm text-amber-700">
          {t("changed", {
            announced: formatCurrency(tax.itemAmount, invoice.currency),
            kept: formatCurrency(invoice.amount, invoice.currency),
          })}
        </p>
      )}
      <div className="space-y-2 rounded-md border p-3 bg-muted/50">
        {invoice.remittanceInformation && (
          <div>
            <p className="text-xs text-muted-foreground">{t("ocr")}</p>
            <p className="text-sm font-mono">{invoice.remittanceInformation}</p>
          </div>
        )}
        {invoice.documentType !== "receivable" && tax.abroad && (
          <div>
            <p className="text-xs text-muted-foreground">{t("abroadLabel")}</p>
            <p className="text-sm font-mono">{t("abroadAccount", { iban: tax.abroad.iban, bic: tax.abroad.bic })}</p>
          </div>
        )}
        <Link to={`/tax-account/${tax.taxAccountId}`} className="text-sm text-primary underline">
          {t("viewOnTaxAccount")}
        </Link>
      </div>
    </div>
  );
};

const InvoiceDetailsDialog: React.FC<InvoiceDetailsDialogProps> = ({
  open,
  onOpenChange,
  invoice,
  handlePayNow,
  updatePaymentDate,
  formatCurrency,
}) => {
  const t = useTranslations("consultantPayments.details");
  const getVerificationDisplay = usePaymentVerificationDisplay();
  const getStatusLabel = useStatusBadge();
  const receiptLabels = useReceiptLabels();
  const { formatDate } = useFormatters();
  const formatOptionalDate = useFormatOptionalDate();
  const [date, setDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    // Reset date when dialog opens with new invoice
    if (open && invoice) {
      if (invoice.paymentDate) {
        setDate(new Date(invoice.paymentDate));
      } else {
        setDate(undefined);
      }
    }
  }, [open, invoice]);


  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate || !invoice || !updatePaymentDate) return;

    setDate(selectedDate);
    updatePaymentDate(invoice.id, selectedDate);
  };

  if (!invoice) return null;

  // #498 Task 6 — design spec AC11: the detail view must show the acknowledging consultant's
  // identity/timestamp for the unmatched-acknowledged path, distinct from an item that was
  // auto-matched or picked from suggestions. Absent entirely for the baseline "no bank accounts
  // connected" flow (paymentVerification is null there — no auto_match_decisions row exists).
  const verificationDisplay = getVerificationDisplay(invoice.paymentVerification);
  const partyKind = partyKindOf(invoice.documentType);

  const detailContent = (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-semibold">{t("labels.status")}</h4>
          <Badge variant={
            invoice.status === "paid" ? "default" :
            invoice.status === "manually_handled" ? "default" :
            invoice.status === "pending" ? "secondary" :
            invoice.status === "overdue" ? "destructive" : "outline"
          }>
            {getStatusLabel(invoice.status)?.label ?? invoice.status}
          </Badge>
        </div>

        <div>
          <h4 className="text-sm font-semibold">{t("labels.type")}</h4>
          <Badge variant="outline">
            {t(`documentTypes.${partyKind}`)}
          </Badge>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold">
          {t(`labels.${PARTY_LABEL_KEY[partyKind]}`)}
        </h4>
        <p>{invoice.vendor || t("notAvailable")}</p>
      </div>

      {invoice.tax && <TaxPayableDetails invoice={invoice} tax={invoice.tax} formatCurrency={formatCurrency} />}

      {/* Vendor Bank Details - Only for payable invoices */}
      {invoice.documentType === 'payable' && (hasVendorAccount(invoice) || invoice.bankBic || invoice.bankName) && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold">{t("labels.vendorBank")}</h4>
          <div className="space-y-2 rounded-md border p-3 bg-muted/50">
            {invoice.vendorIban && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.iban")}</p>
                <p className="text-sm font-mono">{invoice.vendorIban}</p>
              </div>
            )}
            {invoice.vendorBankgiro && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.bankgiro")}</p>
                <p className="text-sm font-mono">{invoice.vendorBankgiro}</p>
              </div>
            )}
            {invoice.vendorPlusgiro && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.plusgiro")}</p>
                <p className="text-sm font-mono">{invoice.vendorPlusgiro}</p>
              </div>
            )}
            {invoice.vendorAccountNumber && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.accountNumber")}</p>
                <p className="text-sm font-mono">{invoice.vendorAccountNumber}</p>
              </div>
            )}
            {invoice.vendorClearingNumber && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.clearingNumber")}</p>
                <p className="text-sm font-mono">{invoice.vendorClearingNumber}</p>
              </div>
            )}
            {invoice.bankBic && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.bic")}</p>
                <p className="text-sm font-mono">{invoice.bankBic}</p>
              </div>
            )}
            {invoice.bankName && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.bankName")}</p>
                <p className="text-sm">{invoice.bankName}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Receipt-specific Details */}
      {invoice.documentType === 'receipt' && (invoice.paymentMethod || invoice.expenseCategory) && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold">{t("labels.receiptDetails")}</h4>
          <div className="space-y-2 rounded-md border p-3 bg-amber-50">
            {invoice.paymentMethod && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.paymentMethod")}</p>
                <p className="text-sm capitalize">{receiptLabels.paymentMethod(invoice.paymentMethod)}</p>
              </div>
            )}
            {invoice.expenseCategory && (
              <div>
                <p className="text-xs text-muted-foreground">{t("labels.expenseCategory")}</p>
                <p className="text-sm capitalize">{invoice.expenseCategory}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold">{t("labels.amount")}</h4>
        <p>{formatCurrency(invoice.amount, invoice.currency)}</p>
        <SekEquivalent sekAmount={invoice.sekAmount} currency={invoice.currency} />
      </div>

      {invoice.remittanceInformation && (
        <div>
          <h4 className="text-sm font-semibold">{t("labels.remittance")}</h4>
          <p>{invoice.remittanceInformation}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-semibold">{t("labels.dueDate")}</h4>
          <p>{formatOptionalDate(invoice.dueDate)}</p>
        </div>

        {invoice.paymentDate && (
          <div>
            <h4 className="text-sm font-semibold">{t("labels.paymentDate")}</h4>
            <p>{formatDate(invoice.paymentDate, "numeric")}</p>
          </div>
        )}
      </div>

      {invoice.approvedBy && (
        <div>
          <h4 className="text-sm font-semibold">{t("labels.approvedBy")}</h4>
          <p>{invoice.approvedBy}</p>
          <p className="text-sm text-muted-foreground">
            {invoice.approvedDate && formatDate(invoice.approvedDate, "numeric")}
          </p>
        </div>
      )}

      {verificationDisplay && (
        <div>
          <h4 className="text-sm font-semibold">{t("labels.verification")}</h4>
          <Badge
            variant="outline"
            className={
              invoice.paymentVerification?.status === "matched"
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
            }
          >
            {verificationDisplay.label}
          </Badge>
          {/* #498 Task 7 — design spec AC13: a later-arriving suggestion for an already
              acknowledged item must be visible on the detail view, not just the row. */}
          <LaterMatchFlag paymentVerification={invoice.paymentVerification} />
          <p className="text-sm text-muted-foreground mt-1">{verificationDisplay.description}</p>
        </div>
      )}

      {/* Payment Date Selection */}
      {updatePaymentDate && invoice.status === "unpaid" && (
        <div>
          <h4 className="text-sm font-semibold mb-2">{t("labels.schedule")}</h4>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : t("pickDate")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateSelect}
                autoFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {handlePayNow && invoice.status === "unpaid" && (
        <Button
          className="w-full"
          onClick={() => handlePayNow(invoice.id)}
        >
          {t("payNow")}
        </Button>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={invoice.filePath ? "max-w-5xl h-[90vh] flex flex-col" : "max-w-4xl"}>
        <DialogHeader>
          <DialogTitle className="text-xl">{t("title")}</DialogTitle>
          <DialogDescription>
            {invoice.vendor} - {invoice.id}
          </DialogDescription>
        </DialogHeader>

        {invoice.filePath ? (
          <ResizablePanelGroup direction="horizontal" className="flex-1 mt-4">
            <ResizablePanel defaultSize={50} minSize={30} className="pr-4">
              <div className="h-full overflow-y-auto pb-4">
                {detailContent}
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={50} minSize={25}>
              <DocumentViewerPanel filePath={invoice.filePath} fileName={invoice.fileName ?? undefined} />
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          <div className="mt-4">
            {detailContent}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceDetailsDialog;
